import { Button, Chip, Input } from "@heroui/react";
import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React, { useState, useRef } from "react";
import ToolPageLayout from "../../components/tools/ToolPageLayout";
import SignUpPromptModal from "../../components/tools/SignUpPromptModal";

const LectureNotes: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState("comprehensive");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const outputFormats = [
    { id: "comprehensive", label: "Comprehensive Notes", icon: "lucide:file-text" },
    { id: "outline", label: "Outline Format", icon: "lucide:list" },
    { id: "cornell", label: "Cornell Notes", icon: "lucide:layout-template" },
    { id: "study", label: "Study Guide", icon: "lucide:book-open" },
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleGenerate = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setShowSignUpModal(true);
    }, 2500);
  };

  const canSubmit = () => {
    return selectedFile !== null || youtubeUrl.trim().length > 0 || hasRecorded;
  };

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "AI Lecture Notes Generator - YouWo AI",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Transform lecture recordings and videos into comprehensive study notes. Free AI-powered lecture notes generator for students.",
  };

  const howItWorksSteps = [
    {
      title: "Add Your Lecture",
      description: "Upload lecture recording, paste YouTube link, or record live in class.",
    },
    {
      title: "AI Takes Notes",
      description: "Our AI listens, identifies key concepts, and organizes information.",
    },
    {
      title: "Study Smart",
      description: "Get comprehensive notes with key terms, summaries, and review questions.",
    },
  ];

  const benefits = [
    {
      title: "100+ Languages",
      description: "Transcribe and create notes from lectures in over 100 languages.",
      icon: "lucide:globe",
    },
    {
      title: "Clickable Timestamps",
      description: "Jump to any topic in the original recording with clickable timestamps.",
      icon: "lucide:clock",
    },
    {
      title: "Key Terms Highlighted",
      description: "Important vocabulary and concepts are automatically identified.",
      icon: "lucide:highlighter",
    },
    {
      title: "Review Questions",
      description: "AI generates review questions to test your understanding.",
      icon: "lucide:help-circle",
    },
    {
      title: "Multiple Note Formats",
      description: "Choose from comprehensive, outline, Cornell method, or study guide format.",
      icon: "lucide:layout-template",
    },
    {
      title: "Works with YouTube",
      description: "Turn any educational YouTube video into structured notes.",
      icon: "lucide:youtube",
    },
  ];

  const faqs = [
    {
      question: "What is the Lecture Notes AI?",
      answer: "Lecture Notes AI is a free tool that transforms lecture recordings, videos, and live recordings into comprehensive, organized study notes. It captures key concepts, definitions, and examples automatically.",
    },
    {
      question: "Can I use it during live lectures?",
      answer: "Yes! You can record lectures live directly in your browser. The AI will transcribe and create notes in real-time or after the recording is complete.",
    },
    {
      question: "Does it work with YouTube lectures?",
      answer: "Absolutely! Just paste any YouTube lecture URL and our AI will generate comprehensive notes from the video content.",
    },
    {
      question: "What note formats are available?",
      answer: "We offer comprehensive notes, outline format, Cornell note method, and study guide format. Choose the style that works best for your learning.",
    },
    {
      question: "Can I add my own notes?",
      answer: "Yes! All AI-generated notes can be edited, annotated, and enhanced with your own additions and comments.",
    },
    {
      question: "Is it useful for exam prep?",
      answer: "Definitely! The study guide format includes review questions and summaries specifically designed to help you prepare for exams.",
    },
  ];

  const relatedTools = [
    {
      name: "AI Flashcard Maker",
      description: "Generate study flashcards automatically from any content.",
      href: "/tools/flashcard-maker",
      icon: "lucide:layers",
      iconColor: "text-purple-500",
    },
    {
      name: "AI Quiz Generator",
      description: "Create practice quizzes to test your understanding of any topic.",
      href: "/tools/quiz-generator",
      icon: "lucide:help-circle",
      iconColor: "text-green-500",
    },
    {
      name: "YouTube Summarizer",
      description: "Transform YouTube videos into concise summaries with timestamps.",
      href: "/tools/youtube-summarizer",
      icon: "lucide:youtube",
      iconColor: "text-red-500",
    },
    {
      name: "PDF Summarizer",
      description: "Turn lengthy PDFs and documents into clear, concise summaries.",
      href: "/tools/pdf-summarizer",
      icon: "lucide:file-text",
      iconColor: "text-orange-500",
    },
  ];

  return (
    <>
      <ToolPageLayout
        title="Free AI Lecture Notes Generator - Smart Note Taking | YouWo AI"
        metaDescription="Transform lecture recordings and videos into comprehensive study notes automatically. Free AI-powered lecture notes generator with key concepts, summaries, and review questions."
        keywords="lecture notes, ai note taking, study notes, class notes, lecture transcription, student notes, note taking app"
        canonicalUrl="https://youwo.ai/tools/lecture-notes"
        structuredData={structuredData}
        heroTitle="Lecture Notes AI"
        heroSubtitle="Transform lectures into comprehensive notes with clickable timestamps. Supports 100+ languages. AI captures key concepts so you can focus on learning."
        heroIcon="lucide:graduation-cap"
        heroIconColor="text-emerald-500"
        heroIconBg="bg-emerald-50"
        howItWorksSteps={howItWorksSteps}
        benefitsTitle="Why Choose YouWo AI Lecture Notes?"
        benefits={benefits}
        faqs={faqs}
        relatedTools={relatedTools}
      >
        {/* Tool Input Area */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8">
          {/* Record Button */}
          <div className="flex justify-center mb-6">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                isRecording
                  ? "bg-red-500 animate-pulse"
                  : "bg-gradient-to-br from-emerald-500 to-emerald-600 hover:shadow-lg"
              }`}
              onClick={() => {
                if (isRecording) {
                  setHasRecorded(true);
                }
                setIsRecording(!isRecording);
              }}
            >
              <Icon
                icon={isRecording ? "lucide:square" : "lucide:mic"}
                className="text-white text-4xl"
              />
            </motion.button>
          </div>

          <p className="text-center text-gray-500 mb-6">
            {isRecording
              ? "Recording lecture... Click to stop"
              : hasRecorded
                ? "Recording ready! Click Generate to continue"
                : "Click to record your lecture live"}
          </p>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-400">or paste a YouTube link</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* YouTube URL Input */}
          <div className="mb-6">
            <Input
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              size="lg"
              startContent={
                <Icon icon="lucide:youtube" className="text-red-500 text-xl" />
              }
              classNames={{
                input: "text-base",
                inputWrapper: "h-14 border-gray-200 hover:border-emerald-500 focus-within:border-emerald-500",
              }}
            />
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-400">or upload a recording</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* File Upload Area */}
          <div
            className={`border-2 border-dashed rounded-xl p-4 text-center transition-colors cursor-pointer ${
              isDragging
                ? "border-emerald-500 bg-emerald-50"
                : selectedFile
                ? "border-green-400 bg-green-50"
                : "border-gray-200 hover:border-emerald-500"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="audio/*,video/*"
              onChange={handleFileSelect}
            />

            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <Icon icon="lucide:file-audio" className="text-green-600 text-xl" />
                <span className="text-gray-900">{selectedFile.name}</span>
                <Button
                  variant="light"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                >
                  Remove
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <Icon icon="lucide:upload" className="text-xl" />
                <span>Upload lecture recording (MP3, MP4, WAV)</span>
              </div>
            )}
          </div>

          {/* Output Format Selector */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-6">
            <span className="text-sm text-gray-600 font-medium">Note Format:</span>
            <div className="flex flex-wrap gap-2">
              {outputFormats.map((format) => (
                <Chip
                  key={format.id}
                  variant={selectedFormat === format.id ? "solid" : "bordered"}
                  color={selectedFormat === format.id ? "primary" : "default"}
                  className={`cursor-pointer transition-all ${
                    selectedFormat === format.id
                      ? "bg-emerald-500 text-white"
                      : "border-gray-200 text-gray-600 hover:border-emerald-500"
                  }`}
                  startContent={<Icon icon={format.icon} className="text-sm" />}
                  onClick={() => setSelectedFormat(format.id)}
                >
                  {format.label}
                </Chip>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-6">
            <Button
              color="primary"
              size="lg"
              className="w-full bg-emerald-500 hover:bg-emerald-600 h-14 font-semibold"
              isLoading={isLoading}
              isDisabled={!canSubmit()}
              onClick={handleGenerate}
            >
              {isLoading ? "Generating Notes..." : "Generate Lecture Notes"}
            </Button>
          </div>
        </div>
      </ToolPageLayout>

      <SignUpPromptModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        toolName="Lecture Notes"
      />
    </>
  );
};

export default LectureNotes;
